import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Toast from '../components/shared/Toast';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const historyRef = useRef([]);
  const lastSuccessRef = useRef(null);
  const lastErrorRef = useRef(null);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type, message, options = {}) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      type,
      message,
      duration: options.duration ?? 4000,
      action: options.action,
    };

    setToasts((prev) => [...prev, toast]);

    // Track in history for MCP agents
    const historyEntry = { type, message, timestamp: Date.now() };
    historyRef.current = [historyEntry, ...historyRef.current.slice(0, 9)];

    if (type === 'success') {
      lastSuccessRef.current = historyEntry;
    } else if (type === 'error') {
      lastErrorRef.current = historyEntry;
    }

    // Update window.__GETCLEARANCE__.notifications if bridge is mounted
    if (window.__GETCLEARANCE__) {
      window.__GETCLEARANCE__.notifications = {
        current: toasts.map(t => ({ type: t.type, message: t.message })),
        history: historyRef.current,
        lastSuccess: lastSuccessRef.current,
        lastError: lastErrorRef.current,
      };
    }

    // Auto-remove after duration (unless duration is 0)
    if (toast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    return id;
  }, [removeToast, toasts]);

  const toast = {
    success: (message, options) => addToast('success', message, options),
    error: (message, options) => addToast('error', message, options),
    warning: (message, options) => addToast('warning', message, options),
    info: (message, options) => addToast('info', message, options),
    remove: removeToast,
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer
        toasts={toasts}
        onRemove={removeToast}
        lastSuccess={lastSuccessRef.current}
        lastError={lastErrorRef.current}
      />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove, lastSuccess, lastError }) {
  const lastToast = toasts[toasts.length - 1];

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      data-testid="toast-container"
      data-has-toast={toasts.length > 0}
      data-last-toast-type={lastToast?.type || null}
      data-last-toast-message={lastToast?.message || null}
      data-last-success={lastSuccess?.message || null}
      data-last-error={lastError?.message || null}
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxWidth: 400,
        pointerEvents: toasts.length === 0 ? 'none' : 'auto',
      }}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => onRemove(toast.id)} />
      ))}
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastContext;
